import apiClient from './apiClient';

/**
 * Region Service - API calls for multi-region functionality
 */

export interface RegionConfig {
  name: string;
  description: string;
  dbServiceUrl: string;
  terminals: string[];
  terminalCount: number;
}

export interface RegionStats {
  totalRegions: number;
  totalTerminals: number;
  defaultRegion: string;
  terminalsByRegion: Record<string, number>;
}

export interface TerminalRegionInfo {
  terminalId: string;
  region: string;
  dbServiceUrl: string;
}

export interface TerminalConfigStatus {
  terminalId: string;
  configured: boolean;
  region?: string;
  dbServiceUrl?: string;
}

export const regionService = {
  /**
   * Get all configured regions
   */
  getAllRegions: async (): Promise<Record<string, RegionConfig>> => {
    const response = await apiClient.get<Record<string, RegionConfig>>('/regions');
    return response;
  },

  /**
   * Get region statistics
   */
  getRegionStats: async (): Promise<RegionStats> => {
    const response = await apiClient.get<RegionStats>('/regions/stats');
    return response;
  },

  /**
   * Get region configuration for a specific terminal
   */
  getRegionForTerminal: async (terminalId: string): Promise<RegionConfig> => {
    const response = await apiClient.get<RegionConfig>(`/regions/terminal/${terminalId}`);
    return response;
  },

  /**
   * Get DB Service URL for a specific terminal
   */
  getDbServiceUrlForTerminal: async (terminalId: string): Promise<TerminalRegionInfo> => {
    const response = await apiClient.get<TerminalRegionInfo>(`/regions/terminal/${terminalId}/db-service-url`);
    return response;
  },

  /**
   * Get all terminals for a specific region
   */
  getTerminalsForRegion: async (regionName: string): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/regions/${regionName}/terminals`);
    return response;
  },

  /**
   * Group terminals by their regions
   */
  groupTerminalsByRegion: async (terminalIds: string[]): Promise<Record<string, string[]>> => {
    const response = await apiClient.post<Record<string, string[]>>('/regions/group-by-region', terminalIds);
    return response;
  },

  /**
   * Get all configured terminal IDs
   */
  getAllTerminals: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/regions/terminals');
    return response;
  },

  /**
   * Check if a terminal is configured
   */
  isTerminalConfigured: async (terminalId: string): Promise<TerminalConfigStatus> => {
    const response = await apiClient.get<TerminalConfigStatus>(`/regions/terminal/${terminalId}/configured`);
    return response;
  },
};

